/**
 * Per-request CSP nonce, threaded through the React tree so root.tsx can
 * stamp `nonce={...}` on every inline `<script>` to satisfy a strict CSP
 * policy (`script-src 'self' 'nonce-...'` — no `'unsafe-inline'`).
 *
 * Why a context (and not just a global): Node SSR is single-threaded but
 * may render multiple requests concurrently inside the same process via
 * async I/O. A module-level `let nonce` would race. React context is
 * scoped to a render pass, so each request's tree sees its own nonce.
 *
 * Usage:
 *   entry.server.tsx wraps <RemixServer> with <NonceProvider value={nonce}>
 *   any component reads via `const nonce = useNonce()`.
 *
 * Audit 2026-05-06: introduced as part of removing `'unsafe-inline'`
 * from the CSP. Empty string fallback in the default keeps tests + dev
 * green when no provider is mounted.
 */
import { createContext, useContext } from "react";

const NonceContext = createContext<string>("");

export const NonceProvider = NonceContext.Provider;

export function useNonce(): string {
  return useContext(NonceContext);
}
